import {
  esquemaCrearDocumento,
  faltaUbicacionFisica,
  MANEJOS,
  type Manejo,
  MIMES_ARCHIVO,
  NIVEL_PROTECCION,
  NIVELES_PROTECCION,
  type NivelProteccion,
  NOMBRE_MANEJO,
  nombreArchivoCanonico,
  plantillaDe,
  seRedacta,
  TAMANO_MAXIMO_ARCHIVO,
} from "@gestiondoc/shared";
import { useMutation, useQuery } from "@tanstack/react-query";
import { notFound, useNavigate, useParams, useRouteContext } from "@tanstack/react-router";
import { type FormEvent, useRef, useState } from "react";
import { queryClient, useTRPC } from "../api.ts";
import {
  CamposControlRegistros,
  CONTROL_VACIO,
  type ValorControl,
} from "../componentes/ControlRegistros.tsx";
import { Dropzone } from "../componentes/Dropzone.tsx";
import { Encabezado } from "../componentes/Encabezado.tsx";
import { SelectorDestinatarios } from "../componentes/ModalesRevision.tsx";
import {
  Aviso,
  Boton,
  BotonEnlace,
  Campo,
  CLASE_ENTRADA,
  SelectorDeContenido,
  Tarjeta,
} from "../componentes/ui.tsx";
import { leerComoBase64, mensajeDe, mimeDeArchivo } from "../utiles.ts";

/**
 * Pantalla 02 · Nuevo documento. Información general, control de registros (solo si el tipo
 * es registro), nivel de protección, archivo y ruta de aprobación. "Guardar borrador" crea el
 * documento; "Enviar a revisión" lo crea y abre el modal de firma en el detalle.
 */
export function PaginaNuevoDocumento() {
  const { codigo } = useParams({ from: "/app/procesos/$codigo/nuevo" });
  const { perfil } = useRouteContext({ from: "/app" });
  const proceso = perfil.procesos.find((p) => p.codigo === codigo);
  if (proceso?.rol !== "carga") throw notFound();

  const trpc = useTRPC();
  const navigate = useNavigate();
  const catalogos = useQuery(trpc.documentos.catalogos.queryOptions({ procesoCodigo: codigo }));
  const cargan = useQuery(trpc.documentos.quienesCargan.queryOptions({ procesoCodigo: codigo }));

  const [tipoCodigo, setTipoCodigo] = useState("");
  const [nombre, setNombre] = useState("");
  const [manejo, setManejo] = useState<Manejo>("digital");
  const [justificacion, setJustificacion] = useState("");
  const [impacto, setImpacto] = useState<string[]>([]);
  const [impactoTexto, setImpactoTexto] = useState("");
  const [nivel, setNivel] = useState<NivelProteccion>(2);
  const [registro, setRegistro] = useState<ValorControl>(CONTROL_VACIO);
  const [archivo, setArchivo] = useState<File | null>(null);
  /** Null = lo que el tipo sugiera: los que tienen plantilla se redactan. */
  const [redactarElegido, setRedactarElegido] = useState<boolean | null>(null);
  const [reviso, setReviso] = useState("");
  const [aprobo, setAprobo] = useState("");
  /**
   * Quien elaboró: por omisión quien crea. Se puede nombrar a otra persona que cargue en el
   * proceso (2026-10-22: Calidad sube los documentos de la Dirección y los revisa); esa persona
   * firma "Elaboró" y quien crea puede quedar de revisor.
   */
  const [elaboro, setElaboro] = useState(perfil.usuario.id);
  /** Fase 3b: solo decide en los informativos; los controlados siempre piden acuse. */
  const [pideAcuse, setPideAcuse] = useState(true);
  /** Comunicado (2026-10-20): a toda la empresa, o solo a las personas elegidas. */
  const [alcance, setAlcance] = useState<"todos" | "personas">("todos");
  const [destinatarios, setDestinatarios] = useState<string[]>([]);
  const [errores, setErrores] = useState<Record<string, string>>({});
  const [errorGeneral, setErrorGeneral] = useState<string | null>(null);
  const enviando = useRef(false);

  const tipo = catalogos.data?.tipos.find((t) => t.codigo === tipoCodigo);
  const esRegistro = tipo?.esRegistro ?? false;
  const puedeRedactar = tipoCodigo !== "" && seRedacta(tipoCodigo);
  /** False = documento informativo: sin ruta de firmas, se publica directo. */
  const requiereAprobacion = tipo?.requiereAprobacion ?? true;
  /** Ruta corta (2026-10-29): el tipo va sin revisor, solo elaboró y aprobó. */
  const conRevisor = tipo?.conRevisor ?? true;
  /** Elaboró otra persona (solo en los tipos con ruta de firmas). */
  const otroElabora = requiereAprobacion && elaboro !== perfil.usuario.id;
  /** True = comunicado: lo ve y lo recibe toda la empresa, o solo las personas elegidas. */
  const paraTodaLaEmpresa = tipo?.paraTodaLaEmpresa ?? false;
  const secciones = (plantillaDe(tipoCodigo) ?? []).map((s) => s.titulo);
  const publicar = useMutation(trpc.documentos.publicar.mutationOptions());
  // Con el permiso temporal se viene a subir lo que ya existe: de entrada, archivo.
  const redactar = puedeRedactar && (redactarElegido ?? !perfil.usuario.cargaSinFirmas);
  const sinFirmas = perfil.usuario.cargaSinFirmas && requiereAprobacion && !redactar;
  const proximo = useQuery({
    ...trpc.documentos.proximoCodigo.queryOptions({ procesoCodigo: codigo, tipoCodigo }),
    enabled: tipoCodigo !== "",
  });

  const crear = useMutation(
    trpc.documentos.crear.mutationOptions({
      onError: (e) => setErrorGeneral(mensajeDe(e)),
    }),
  );

  async function enviar(e: FormEvent, firmarDespues: boolean) {
    e.preventDefault();
    // Leer el archivo tarda: sin esto, dos clics seguidos crean dos documentos.
    if (enviando.current) return;
    enviando.current = true;
    try {
      await enviarUnaVez(firmarDespues);
    } finally {
      enviando.current = false;
    }
  }

  async function enviarUnaVez(firmarDespues: boolean) {
    setErrores({});
    setErrorGeneral(null);
    const mime = archivo ? mimeDeArchivo(archivo) : null;
    if (!redactar) {
      if (!archivo) {
        setErrores({ archivo: "Adjunta el archivo del documento" });
        return;
      }
      if (!mime) {
        setErrores({ archivo: "Solo se aceptan Word, Excel o PDF" });
        return;
      }
      if (archivo.size > TAMANO_MAXIMO_ARCHIVO) {
        setErrores({ archivo: "El archivo supera los 25 MB" });
        return;
      }
    }
    // Guardar el borrador nunca se bloquea; enviar a revisión sí necesita la ubicación del papel.
    if (firmarDespues && esRegistro && faltaUbicacionFisica(manejo, registro.ubicacion)) {
      setErrores({
        "registro.ubicacion": "Indica dónde se archiva la copia física para enviar a revisión",
      });
      return;
    }
    const adjunto =
      !redactar && archivo && mime
        ? { nombre: archivo.name, mime, base64: await leerComoBase64(archivo) }
        : undefined;
    const datos = esquemaCrearDocumento.safeParse({
      procesoCodigo: codigo,
      tipoCodigo,
      nombre,
      manejo,
      justificacion,
      impacto,
      nivelProteccion: nivel,
      registro: esRegistro ? registro : undefined,
      pideAcuse,
      destinatarios: paraTodaLaEmpresa && alcance === "personas" ? destinatarios : [],
      ruta:
        requiereAprobacion && !sinFirmas
          ? { ...(conRevisor ? { reviso } : {}), aprobo }
          : undefined,
      elaboro: otroElabora && !sinFirmas ? elaboro : undefined,
      archivo: adjunto,
      sinFirmas: sinFirmas || undefined,
    });
    if (paraTodaLaEmpresa && alcance === "personas" && destinatarios.length === 0) {
      setErrores({ destinatarios: "Elige al menos a una persona, o manda el comunicado a todos" });
      return;
    }
    if (!datos.success) {
      const porCampo: Record<string, string> = {};
      for (const issue of datos.error.issues) {
        const clave = issue.path.join(".");
        if (!porCampo[clave]) porCampo[clave] = issue.message;
      }
      setErrores(porCampo);
      return;
    }
    const creado = await crear.mutateAsync(datos.data).catch(() => null);
    if (!creado) return;
    // Un informativo con archivo se publica en el mismo paso, sin ruta de firmas.
    if (!requiereAprobacion && firmarDespues && !redactar) {
      await publicar
        .mutateAsync({ revisionId: creado.revisionId })
        .catch((e) => setErrorGeneral(mensajeDe(e)));
      queryClient.invalidateQueries();
      navigate({ to: "/documentos/$codigo", params: { codigo: creado.codigo }, search: {} });
      return;
    }
    queryClient.invalidateQueries();
    if (redactar) {
      navigate({ to: "/documentos/$codigo/editar", params: { codigo: creado.codigo } });
      return;
    }
    navigate({
      to: "/documentos/$codigo",
      params: { codigo: creado.codigo },
      search: firmarDespues && !sinFirmas ? { firmar: true } : {},
    });
  }

  const personas = catalogos.data?.personas ?? [];
  const yoComoPersona = {
    id: perfil.usuario.id,
    nombre: perfil.usuario.nombre,
    puesto: perfil.usuario.puesto,
  };
  // Revisor y aprobador: cualquiera menos quien elaboró. Si elaboró otra persona, quien crea
  // también puede ir en la ruta (es el caso de Calidad: sube, revisa, y la Dirección aprueba).
  const candidatos = (otroElabora ? [...personas, yoComoPersona] : personas)
    .filter((p) => p.id !== elaboro)
    .sort((a, b) => a.nombre.localeCompare(b.nombre, "es"));
  const elaboradores = (cargan.data ?? []).filter((p) => p.id !== perfil.usuario.id);
  function cambiarElaboro(id: string) {
    setElaboro(id);
    if (reviso === id) setReviso("");
    if (aprobo === id) setAprobo("");
  }
  const mimeElegido = archivo ? mimeDeArchivo(archivo) : null;
  const nombreCanonico =
    proximo.data && mimeElegido
      ? nombreArchivoCanonico(proximo.data, 1, MIMES_ARCHIVO[mimeElegido])
      : null;

  return (
    <form onSubmit={(e) => enviar(e, false)} className="max-w-[1180px]">
      <Encabezado
        overline={`${proceso.nombre} › Documentos`}
        titulo="Nuevo documento"
        acciones={
          <>
            <BotonEnlace to="/procesos/$codigo" params={{ codigo }} variante="discreto">
              Cancelar
            </BotonEnlace>
            {redactar ? (
              <Boton type="submit" disabled={crear.isPending}>
                {crear.isPending ? "Guardando…" : "Guardar y redactar"}
              </Boton>
            ) : sinFirmas ? (
              <Boton variante="firma" type="submit" disabled={crear.isPending}>
                {crear.isPending ? "Publicando…" : "Publicar sin firmas"}
              </Boton>
            ) : (
              <>
                <Boton variante="secundario" type="submit" disabled={crear.isPending}>
                  {crear.isPending ? "Guardando…" : "Guardar borrador"}
                </Boton>
                {/* Si elaboró otra persona, la firma que envía a revisión es de ella, no de quien sube. */}
                {!otroElabora && (
                  <Boton onClick={(e) => enviar(e, true)} disabled={crear.isPending}>
                    {!requiereAprobacion
                      ? "Publicar"
                      : conRevisor
                        ? "Enviar a revisión"
                        : "Enviar a aprobación"}
                  </Boton>
                )}
              </>
            )}
          </>
        }
      />

      {errorGeneral && <Aviso className="mb-4">{errorGeneral}</Aviso>}
      {sinFirmas && (
        <Aviso tono="ambar" className="mb-4">
          Tienes el permiso temporal: este archivo queda vigente de una vez, sin ruta de firmas.
        </Aviso>
      )}

      <div className="grid gap-5 lg:grid-cols-[1fr_360px]">
        <div className="space-y-5">
          <Tarjeta
            titulo="Información general"
            ayuda="El código se genera con el proceso y el tipo. La primera versión siempre es Rev. 01."
          >
            <div className="grid gap-4 sm:grid-cols-2">
              <Campo etiqueta="Proceso">
                <input value={proceso.nombre} disabled className={CLASE_ENTRADA} />
              </Campo>
              <Campo etiqueta="Tipo de documento" error={errores.tipoCodigo}>
                <select
                  value={tipoCodigo}
                  onChange={(e) => setTipoCodigo(e.target.value)}
                  className={CLASE_ENTRADA}
                >
                  <option value="">Elige un tipo…</option>
                  {catalogos.data?.tipos.map((t) => (
                    <option key={t.codigo} value={t.codigo}>
                      {t.nombre}
                    </option>
                  ))}
                </select>
              </Campo>
              <div className="sm:col-span-2">
                <Campo etiqueta="Nombre del documento" error={errores.nombre}>
                  <input
                    value={nombre}
                    onChange={(e) => setNombre(e.target.value)}
                    placeholder="Registro de asistencia a reuniones"
                    className={CLASE_ENTRADA}
                  />
                </Campo>
              </div>
              <Campo etiqueta="Código asignado" ayuda="Autogenerado al guardar.">
                <input
                  value={proximo.data ?? ""}
                  readOnly
                  placeholder="—"
                  className={`${CLASE_ENTRADA} bg-[#fbfbfd] font-mono font-semibold text-marino`}
                />
              </Campo>
              <Campo etiqueta="Manejo" ayuda={AYUDA_MANEJO[manejo]}>
                <select
                  value={manejo}
                  onChange={(e) => setManejo(e.target.value as Manejo)}
                  className={CLASE_ENTRADA}
                >
                  {MANEJOS.map((m) => (
                    <option key={m} value={m}>
                      {NOMBRE_MANEJO[m]}
                    </option>
                  ))}
                </select>
              </Campo>
              <div className="sm:col-span-2">
                <Campo etiqueta="Justificación" error={errores.justificacion}>
                  <textarea
                    value={justificacion}
                    onChange={(e) => setJustificacion(e.target.value)}
                    rows={3}
                    placeholder="Para qué existe el documento y qué evidencia deja."
                    className={CLASE_ENTRADA}
                  />
                </Campo>
              </div>
              <div className="sm:col-span-2">
                <Campo
                  etiqueta="Impacto"
                  ayuda="Escribe el código y aparece en la lista."
                  error={errores.impacto}
                >
                  <input
                    list="documentos-existentes"
                    value={impactoTexto}
                    onChange={(e) => setImpactoTexto(e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key === "Enter") {
                        e.preventDefault();
                        agregarImpacto();
                      }
                    }}
                    onBlur={agregarImpacto}
                    placeholder="Documentos o programas que se ven afectados… (ej. GES-PRO-003)"
                    className={CLASE_ENTRADA}
                  />
                  <datalist id="documentos-existentes">
                    {catalogos.data?.existentes.map((d) => (
                      <option key={d.codigo} value={d.codigo}>
                        {d.nombre}
                      </option>
                    ))}
                  </datalist>
                </Campo>
                {impacto.length > 0 && (
                  <div className="mt-2 flex flex-wrap gap-1.5">
                    {impacto.map((c) => (
                      <button
                        key={c}
                        type="button"
                        onClick={() => setImpacto(impacto.filter((x) => x !== c))}
                        className="rounded-full bg-marino-claro px-2.5 py-0.5 font-mono text-[11.5px] text-marino"
                        title="Quitar"
                      >
                        {c} ×
                      </button>
                    ))}
                  </div>
                )}
              </div>
            </div>
          </Tarjeta>

          {esRegistro && (
            <Tarjeta
              titulo="Control de registros"
              etiqueta="Se muestra porque el tipo es Registro"
              ayuda="Cuánto tiempo se guarda y, si hay papel, dónde. Esto alimenta el reporte de auditoría."
              acento
            >
              <CamposControlRegistros
                codigo={proximo.data ?? null}
                nombre={nombre}
                puestoPorDefecto={perfil.usuario.puesto}
                manejo={manejo}
                valor={registro}
                onCambio={setRegistro}
                errores={errores}
              />
            </Tarjeta>
          )}

          <Tarjeta titulo="Nivel de protección" ayuda="Quién puede ver este documento.">
            <div className="grid gap-2 sm:grid-cols-2 xl:grid-cols-4">
              {NIVELES_PROTECCION.map((n) => (
                <button
                  key={n}
                  type="button"
                  onClick={() => setNivel(n)}
                  className={`rounded-tarjeta border p-3 text-left ${
                    nivel === n
                      ? "border-[1.5px] border-marino bg-marino-claro"
                      : "border-borde bg-white hover:bg-fondo"
                  }`}
                >
                  <div className="text-[13px] font-bold text-marino">
                    {n} · {NIVEL_PROTECCION[n].nombre}
                  </div>
                  <div className="text-[11.5px] text-texto-3">{NIVEL_PROTECCION[n].quienVe}</div>
                </button>
              ))}
            </div>
          </Tarjeta>
        </div>

        <div className="space-y-5">
          <Tarjeta
            titulo="Contenido"
            ayuda={
              redactar
                ? "Se redacta en la app con la plantilla de SMG; el PDF sale de lo que escribes."
                : "Word, Excel o PDF. Se convierte a PDF con sello al aprobarse."
            }
          >
            {puedeRedactar && (
              <SelectorDeContenido redactar={redactar} onCambio={setRedactarElegido} />
            )}
            {redactar ? (
              <p className="text-[12.5px] text-texto-3">
                Al guardar se abre el editor con{" "}
                {secciones.length === 1
                  ? `una sola sección: ${secciones[0]}.`
                  : `las secciones de ${tipo?.nombre.toLowerCase()}: ${secciones.slice(0, 4).join(", ").toLowerCase()}…`}{" "}
                Encabezado, pie y numeración los pone el sistema.
              </p>
            ) : (
              <>
                <Dropzone archivo={archivo} onArchivo={setArchivo} error={errores.archivo} />
                {archivo && nombreCanonico && (
                  <div className="mt-2 text-[11.5px] text-texto-3">
                    Se guardará como <span className="font-mono text-marino">{nombreCanonico}</span>
                  </div>
                )}
              </>
            )}
          </Tarjeta>

          {!requiereAprobacion && (
            <Tarjeta
              titulo="Publicación"
              ayuda="Un documento informativo se publica sin ruta de firmas ni revisión anual."
              etiqueta="Documento informativo"
              acento
            >
              {paraTodaLaEmpresa && (
                <div className="mb-4">
                  <SelectorDestinatarios
                    personas={personas.filter((p) => p.id !== perfil.usuario.id)}
                    alcance={alcance}
                    onAlcance={setAlcance}
                    elegidos={destinatarios}
                    onElegidos={setDestinatarios}
                    error={errores.destinatarios}
                  />
                </div>
              )}
              <label className="flex items-start gap-2 text-[13px]">
                <input
                  type="checkbox"
                  checked={pideAcuse}
                  onChange={(e) => setPideAcuse(e.target.checked)}
                  className="mt-0.5"
                />
                <span>
                  {paraTodaLaEmpresa
                    ? "Pedir acuse de lectura a quien lo recibe"
                    : "Pedir acuse de lectura al personal del proceso"}
                  <span className="block text-[11.5px] text-texto-3">
                    Cada persona recibe el aviso; con acuse, además queda constancia de que lo leyó.
                  </span>
                </span>
              </label>
            </Tarjeta>
          )}
          {requiereAprobacion && !sinFirmas && (
            <Tarjeta
              titulo="Ruta de aprobación"
              ayuda={
                conRevisor
                  ? "Cada paso pide firma desde la app."
                  : "Este tipo va sin revisor: elaboró y aprobó, dos firmas desde la app."
              }
            >
              <ol className="space-y-3">
                <Paso n={1} etiqueta="Elaboró" activo={!otroElabora}>
                  {elaboradores.length > 0 ? (
                    <select
                      value={elaboro}
                      onChange={(e) => cambiarElaboro(e.target.value)}
                      className={CLASE_ENTRADA}
                    >
                      <option value={perfil.usuario.id}>{perfil.usuario.nombre} · tú</option>
                      {elaboradores.map((p) => (
                        <option key={p.id} value={p.id}>
                          {p.nombre} · {p.puesto}
                        </option>
                      ))}
                    </select>
                  ) : (
                    <>
                      <div className="text-[13px] font-semibold">{perfil.usuario.nombre} · tú</div>
                      <div className="text-[11.5px] text-texto-3">{perfil.usuario.puesto}</div>
                    </>
                  )}
                  {elaboradores.length > 0 && (
                    <div className="mt-1 text-[11.5px] text-texto-3">
                      {otroElabora
                        ? "Le llegará el aviso para firmar como Elaboró; mientras, tú también puedes editar el borrador."
                        : "Si subes el documento por otra persona, elígela: ella firma como Elaboró y tú puedes revisarlo."}
                    </div>
                  )}
                </Paso>
                {conRevisor && (
                  <Paso n={2} etiqueta="Revisó">
                    <select
                      value={reviso}
                      onChange={(e) => setReviso(e.target.value)}
                      className={CLASE_ENTRADA}
                    >
                      <option value="">Elige quién revisa…</option>
                      {candidatos.map((p) => (
                        <option key={p.id} value={p.id}>
                          {p.nombre} · {p.puesto}
                        </option>
                      ))}
                    </select>
                    {errores["ruta.reviso"] && (
                      <div className="mt-1 text-[11.5px] text-rojo">{errores["ruta.reviso"]}</div>
                    )}
                  </Paso>
                )}
                <Paso n={conRevisor ? 3 : 2} etiqueta="Aprobó">
                  <select
                    value={aprobo}
                    onChange={(e) => setAprobo(e.target.value)}
                    className={CLASE_ENTRADA}
                  >
                    <option value="">Elige quién aprueba…</option>
                    {candidatos.map((p) => (
                      <option key={p.id} value={p.id}>
                        {p.nombre} · {p.puesto}
                      </option>
                    ))}
                  </select>
                  {errores["ruta.aprobo"] && (
                    <div className="mt-1 text-[11.5px] text-rojo">{errores["ruta.aprobo"]}</div>
                  )}
                </Paso>
              </ol>
            </Tarjeta>
          )}

          <Tarjeta titulo="Importar historial">
            <p className="text-[12.5px] text-texto-3">
              ¿Ya existe con revisiones previas? Puedes importar el historial completo (Rev. 01, 02,
              03…) en un solo paso en vez de subirlas una por una.
            </p>
            {perfil.usuario.rolGlobal === "administrador" ? (
              <BotonEnlace
                to="/historial/importar"
                search={{ proceso: codigo }}
                className="mt-3 inline-block"
              >
                Importar historial
              </BotonEnlace>
            ) : (
              <p className="mt-2 text-[11.5px] text-texto-3">
                Lo hace un administrador: pídeselo con los archivos de cada revisión.
              </p>
            )}
          </Tarjeta>
        </div>
      </div>
    </form>
  );

  function agregarImpacto() {
    const c = impactoTexto.trim().toUpperCase();
    if (!c) return;
    if (!impacto.includes(c)) setImpacto([...impacto, c]);
    setImpactoTexto("");
  }
}

const AYUDA_MANEJO: Record<Manejo, string> = {
  digital: "Se consulta y se llena en pantalla.",
  fisico: "Se imprime y se archiva en papel.",
  fisico_y_digital: "Se imprime, se llena a mano y se escanea.",
};

function Paso({
  n,
  etiqueta,
  activo,
  children,
}: {
  n: number;
  etiqueta: string;
  activo?: boolean;
  children: React.ReactNode;
}) {
  return (
    <li className="flex gap-3">
      <div
        className={`mt-0.5 grid size-6 shrink-0 place-items-center rounded-full text-[11px] font-bold ${
          activo ? "bg-marino text-white" : "bg-gris-fondo text-gris"
        }`}
      >
        {n}
      </div>
      <div className="min-w-0 flex-1">
        <div className="mb-1 text-[10.5px] font-semibold tracking-[.08em] text-texto-3 uppercase">
          {etiqueta}
        </div>
        {children}
      </div>
    </li>
  );
}
